import { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { Helmet } from "react-helmet-async"
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom"
import { CheckCircle2, Clock, ArrowLeft, Calendar, ChevronRight, Star, Award, Shield, Users, X } from "lucide-react"
import { Button } from "../../components/ui/button"
import { Input } from "../../components/ui/input"
import { Dialog, DialogContent } from "../../components/ui/dialog"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select"
import { getCookie, setCookie } from "../../utils/cookies"
import api from "../../utils/api"
import { format } from "date-fns"

export default function PackageDetail() {
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const locationId = searchParams.get("location") || ""
  const navigate = useNavigate()
  const [pkg, setPkg] = useState(null)
  const [loading, setLoading] = useState(true)

  // Booking flow states
  const [loggedInUser, setLoggedInUser] = useState(null)
  const [showPhonePopup, setShowPhonePopup] = useState(false)
  const [showOtpPopup, setShowOtpPopup] = useState(false)
  const [showNotFoundPopup, setShowNotFoundPopup] = useState(false)
  const [showRegistrationPopup, setShowRegistrationPopup] = useState(false)
  const [showPatientSelectPopup, setShowPatientSelectPopup] = useState(false)
  const [showThankYou, setShowThankYou] = useState(false)
  const [signupMode, setSignupMode] = useState(false)
  const [mobileNumber, setMobileNumber] = useState("")
  const [phoneError, setPhoneError] = useState("")
  const [otpValue, setOtpValue] = useState("")
  const [otpError, setOtpError] = useState("")
  const [timer, setTimer] = useState(30)
  const [savedPatients, setSavedPatients] = useState([])
  const [selectedPatient, setSelectedPatient] = useState(null)
  const [appointmentDate, setAppointmentDate] = useState("")
  const [clinics, setClinics] = useState([])
  const [selectedClinic, setSelectedClinic] = useState("")
  const [bookingDetails, setBookingDetails] = useState(null)
  const [showPaymentPopup, setShowPaymentPopup] = useState(false)
  const [payLoading, setPayLoading] = useState(false)
  const [payMode, setPayMode] = useState(null)
  const [payDetails, setPayDetails] = useState(null)
  const [relations, setRelations] = useState([])
  const [states, setStates] = useState([])
  const [registrationData, setRegistrationData] = useState({
    firstName: "", lastName: "", dob: "", relation: "", gender: "Male", stateId: "",
  })

  // Fetch clinics
  useEffect(() => {
    api.get("/apis/web/clinics").then(res => {
      if (res.data?.status === 0 && Array.isArray(res.data.result)) {
        setClinics(res.data.result.map(c => ({ id: c.Hospital_Id, name: c.HospitalName })))
      }
    }).catch(() => {})
  }, [])

  // Fetch relations & states for registration
  useEffect(() => {
    api.get("/apis/web/relations").then(res => {
      if (res.data?.success && Array.isArray(res.data.data?.records)) {
        setRelations(res.data.data.records.map(r => ({ id: r.RELATION_ID, name: r.RELATION_NAME })))
      }
    }).catch(() => {})
    api.get("/apis/web/states").then(res => {
      if (res.data?.success && Array.isArray(res.data.data)) {
        setStates(res.data.data.map(s => ({ id: s.state_id, name: s.state_name })))
      }
    }).catch(() => {})
  }, [])

  // OTP timer
  useEffect(() => {
    if (timer <= 0) return
    const interval = setInterval(() => setTimer(prev => prev - 1), 1000)
    return () => clearInterval(interval)
  }, [timer])

  // Check logged in user
  useEffect(() => {
    const savedUser = getCookie("user")
    if (savedUser && savedUser !== "undefined") {
      try {
        const u = JSON.parse(savedUser)
        setLoggedInUser(u)
        const phone = (u.phone || u.mobile || u.mobileNo || "").replace(/\D/g, "").replace(/^91/, "")
        if (phone) fetchPatientsByMobile(phone).then(patients => setSavedPatients(patients))
      } catch (e) {}
    }
    const handleUserUpdated = (e) => {
      setLoggedInUser(e.detail)
      if (!e.detail) setSavedPatients([])
    }
    window.addEventListener("user-updated", handleUserUpdated)
    return () => window.removeEventListener("user-updated", handleUserUpdated)
  }, [])

  const fetchPatientsByMobile = async (mobile) => {
    try {
      const res = await api.get(`/apis/web/patientbymobilename?mobile=91${mobile}`)
      if (res.data.status === 0 && Array.isArray(res.data.result)) {
        return res.data.result.map((p, index) => ({
          id: index + 1, name: p.patient_name, uid: p.patientmr_no,
          regDate: p.Created_on ? format(new Date(p.Created_on), "dd/MM/yyyy") : ""
        }))
      }
    } catch (err) { console.error("Failed to fetch patients list", err) }
    return []
  }

  const handleBookNow = () => {
    if (loggedInUser) {
      setShowPatientSelectPopup(true)
    } else {
      setShowPhonePopup(true)
    }
  }

  const handlePhoneConfirm = async () => {
    if (!mobileNumber || mobileNumber.length !== 10 || !/^[6-9]\d{9}$/.test(mobileNumber)) {
      setPhoneError("Please enter a valid 10-digit mobile number")
      return
    }
    setPhoneError("")
    try {
      const res = await api.post("/apis/web/checkPatientAndSendOTP", new URLSearchParams({ mobileNo: `91${mobileNumber}` }), {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      })
      if (res.data.status === 0) {
        setSignupMode(false); setTimer(30); setShowPhonePopup(false); setShowOtpPopup(true)
      } else if (res.data.status === 2) {
        setShowNotFoundPopup(true)
      } else {
        alert(res.data.message || "Failed to send OTP")
      }
    } catch (e) { alert("Network error. Please try again.") }
  }

  const handleRegisterYes = async () => {
    try {
      const res = await fetch("/apis/web/sendotp", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mobileNo: mobileNumber }),
      })
      const data = await res.json()
      if (data.status === 0) {
        setShowNotFoundPopup(false); setSignupMode(true); setTimer(30); setShowPhonePopup(false); setShowOtpPopup(true)
      } else { alert(data.message || "Failed to send OTP") }
    } catch (e) { alert("Failed to send OTP") }
  }

  const handleResendOTP = async () => {
    if (signupMode) {
      try {
        await fetch("/apis/web/sendotp", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ mobileNo: mobileNumber }) })
        setTimer(30)
      } catch { alert("Failed to resend OTP") }
      return
    }
    try {
      await api.post("/apis/web/checkPatientAndSendOTP", new URLSearchParams({ mobileNo: `91${mobileNumber}` }), {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      })
      setTimer(30)
    } catch { alert("Failed to resend OTP") }
  }

  const handleOtpConfirm = async () => {
    if (!otpValue || otpValue.length < 4) { alert("Please enter a valid OTP"); return }
    setOtpError("")
    if (signupMode) {
      try {
        const res = await fetch("/apis/web/verifysignupOTP", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ mobileNo: mobileNumber, otp: otpValue }),
        })
        const data = await res.json()
        if (data.status === 0) { setShowOtpPopup(false); setOtpValue(""); setShowRegistrationPopup(true) }
        else { setOtpError(data.message || "Invalid OTP") }
      } catch (e) { setOtpError("Something went wrong") }
      return
    }
    try {
      const res = await api.post("/apis/web/verifyOTP", new URLSearchParams({ mobileNo: `91${mobileNumber}`, otp: otpValue }), {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      })
      if (res.data.status === 0) {
        setCookie("accessToken", res.data.accessToken, 1)
        setCookie("refreshToken", res.data.refreshToken, 7)
        setCookie("user", JSON.stringify(res.data.user), 7)
        setLoggedInUser(res.data.user)
        const patients = await fetchPatientsByMobile(mobileNumber)
        setSavedPatients(patients)
        window.dispatchEvent(new CustomEvent("user-updated", { detail: res.data.user }))
        setShowOtpPopup(false); setOtpValue(""); setShowPatientSelectPopup(true)
      } else { setOtpError(res.data.message || "Invalid OTP") }
    } catch (e) { setOtpError("Network error. Please try again.") }
  }

  const handleRegistrationSubmit = async (e) => {
    e.preventDefault()
    try {
      let mobile = mobileNumber
      if (!mobile) {
        try { const u = JSON.parse(getCookie("user") || "{}"); mobile = (u?.mobile || u?.phone || u?.mobileNo || "").replace(/\D/g, "").replace(/^91/, "") } catch {}
      }
      const genderMap = { Male: "M", Female: "F", Other: "O" }
      if (signupMode) {
        const res = await fetch("/apis/web/signup", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            FirstName: registrationData.firstName, LastName: registrationData.lastName,
            Dob: registrationData.dob, Gender: genderMap[registrationData.gender] || registrationData.gender,
            Mobile: mobile, Relation: "000", stateId: registrationData.stateId,
            CountryMobileCode: "+91", country: "India",
          }),
        })
        const data = await res.json()
        if (data.status === 0) {
          const userData = data.user || { name: `${registrationData.firstName} ${registrationData.lastName}`, mobile, patientId: data.mrno || "" }
          if (data.accessToken) setCookie("accessToken", data.accessToken, 1)
          if (data.refreshToken) setCookie("refreshToken", data.refreshToken, 7)
          setCookie("user", JSON.stringify(userData), 7)
          setLoggedInUser(userData)
          window.dispatchEvent(new CustomEvent("user-updated", { detail: userData }))
          const patients = await fetchPatientsByMobile(mobile)
          setSavedPatients(patients)
          setShowRegistrationPopup(false); setShowPatientSelectPopup(true)
          setRegistrationData({ firstName: "", lastName: "", dob: "", relation: "", gender: "Male", stateId: "" })
          setSignupMode(false)
        } else { alert(data.message || "Registration failed") }
      } else {
        const res = await api.post("/apis/web/registerpatient", {
          FirstName: registrationData.firstName, LastName: registrationData.lastName,
          Dob: registrationData.dob, Gender: genderMap[registrationData.gender] || registrationData.gender,
          Mobile: mobile, Relation: registrationData.relation, stateId: registrationData.stateId,
          CountryMobileCode: "+91", country: "India",
        })
        if (res.data.success || res.data.status === 0) {
          const patients = await fetchPatientsByMobile(mobile)
          setSavedPatients(patients)
          setShowRegistrationPopup(false)
          setRegistrationData({ firstName: "", lastName: "", dob: "", relation: "", gender: "Male", stateId: "" })
        } else { alert(res.data.message || "Registration failed") }
      }
    } catch (error) { console.error(error); alert("Registration failed") }
  }

  const handleConfirmBooking = () => {
    if (!appointmentDate || !selectedPatient) { alert("Please select appointment date and patient"); return }
    const details = {
      patientName: selectedPatient.name,
      patientId: selectedPatient.uid,
      packageName: pkg?.servName,
      packagePrice: pkg?.serviceCost,
      location: pkg?.hospitalName || "",
      locationId: locationId,
      appointmentDate: format(new Date(appointmentDate), "dd MMM yyyy"),
      appointmentDateRaw: appointmentDate,
      bookingDate: format(new Date(), "dd MMM yyyy"),
    }
    setBookingDetails(details)
    setShowPatientSelectPopup(false)
    setShowPaymentPopup(true)
  }

  const handlePaymentComplete = async () => {
    if (!bookingDetails) return
    setPayLoading(true)
    setPayMode("online")

    const userStr = getCookie("user")
    let mobile = mobileNumber
    if (!mobile && userStr && userStr !== "undefined") {
      try { const u = JSON.parse(userStr); mobile = (u?.mobile || u?.phone || u?.mobileNo || "").replace(/\D/g, "").replace(/^91/, "") } catch {}
    }

    try {
      const res = await api.post("/apis/web/createorder", {
        amount: parseFloat(bookingDetails.packagePrice),
        patientId: bookingDetails.patientId,
        doctorId: "",
      })
      const data = res.data
      if (data.status === 0 && data.orderId) {
        const options = {
          key: process.env.REACT_APP_RAZORPAY_KEY,
          name: "Pranaam Hospitals",
          description: `Package - ${bookingDetails.packageName}`,
          image: "/images/pranaam-logo.png",
          order_id: data.orderId,
          prefill: { name: bookingDetails.patientName, contact: mobile },
          handler: async function (response) {
            const finalDetails = {
              ...bookingDetails,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpayOrderId: response.razorpay_order_id,
              razorpaySignature: response.razorpay_signature,
              orderId: data.orderId,
              paymentStatus: "Paid",
              mobile,
            }
            try {
              const tests = [{ servCode: pkg?.servCode }]
              const bookingRes = await api.post("/apis/web/createpackagebooking", {
                patientId: finalDetails.patientId,
                appointmentDate: finalDetails.appointmentDateRaw,
                mobile: mobile,
                totalAmount: finalDetails.packagePrice,
                tests: tests,
                paymentDetails: {
                  orderDtls: {
                    ORDERNO: finalDetails.razorpayOrderId,
                    PAYMENTID: finalDetails.razorpayPaymentId,
                    SIGNATURE: finalDetails.razorpaySignature,
                  }
                }
              })
              if (bookingRes.data?.success) {
                finalDetails.billNo = bookingRes.data.billNo || ""
                finalDetails.receiptNo = bookingRes.data.receiptNo || ""
              }
            } catch (err) {
              console.error("Create package booking failed:", err)
            }
            setPayDetails(finalDetails)
            setShowPaymentPopup(false)
            setShowThankYou(true)
          },
          modal: {
            ondismiss: function () { alert("Payment failed. Please try again."); setPayLoading(false) },
            confirm_close: true,
          },
          retry: { enabled: true, max_count: 3 },
          timeout: 300,
          theme: { color: "#dc2626" },
        }
        const rzp = new window.Razorpay(options)
        rzp.on('payment.failed', function (response) { alert(response.error?.description || "Payment failed. Please try again."); setPayLoading(false) })
        rzp.open()
      } else { alert(data.message || "Failed to create order") }
    } catch (err) { console.error("Create order failed:", err); alert("Payment failed. Please try again.") }
    setPayLoading(false)
  }



  useEffect(() => {
    if (!id || !locationId) { setLoading(false); return }
    api.get(`/apis/web/packagesbylocationandid?hospitalId=${locationId}&servCode=${id}`)
      .then(res => {
        if (res.data?.success && Array.isArray(res.data.data) && res.data.data.length > 0) {
          setPkg(res.data.data[0])
        } else {
          setPkg(null)
        }
      })
      .catch(err => { console.error("Error fetching package:", err); setPkg(null) })
      .finally(() => setLoading(false))
  }, [id, locationId])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    )
  }

  if (!pkg) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Package Not Found</h2>
        <p className="text-gray-500 mb-6">The package you're looking for doesn't exist.</p>
        <Button onClick={() => navigate("/packages")}>Back to Packages</Button>
      </div>
    )
  }

  if (showThankYou) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center py-8 px-4">
        <div className="max-w-3xl w-full mx-auto">
          <div className="text-center mb-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">{payDetails?.paymentStatus === "Paid" ? "Payment Successful" : "Booking Confirmed"}</h1>
            <p className="text-gray-600">Your package booking has been successfully placed</p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6 pb-3 border-b">Booking Details</h2>
            <div className="space-y-4">
              <div className="flex justify-between items-start">
                <span className="text-gray-600 font-medium">Patient Name</span>
                <span className="font-semibold text-gray-900 text-right">{payDetails?.patientName}</span>
              </div>
              <div className="flex justify-between items-start">
                <span className="text-gray-600 font-medium">Patient ID</span>
                <span className="font-semibold text-primary text-right">{payDetails?.patientId}</span>
              </div>
              <div className="flex justify-between items-start">
                <span className="text-gray-600 font-medium">Package</span>
                <span className="font-semibold text-gray-900 text-right">{payDetails?.packageName}</span>
              </div>
              <div className="flex justify-between items-start">
                <span className="text-gray-600 font-medium">Location</span>
                <span className="font-semibold text-gray-900 text-right">{payDetails?.location}</span>
              </div>
              <div className="flex justify-between items-start">
                <span className="text-gray-600 font-medium">Appointment Date</span>
                <span className="font-semibold text-gray-900 text-right">{payDetails?.appointmentDate}</span>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6 pb-3 border-b">Payment Details</h2>
            <div className="space-y-4">
              <div className="flex justify-between items-start">
                <span className="text-gray-600 font-medium">{payDetails?.paymentStatus === "Paid" ? "Amount Paid" : "Amount Due"}</span>
                <span className={`font-bold text-right ${payDetails?.paymentStatus === "Paid" ? "text-green-600" : "text-orange-500"}`}>Rs. {payDetails?.packagePrice || 0}/-</span>
              </div>
              {payDetails?.orderId && (
                <div className="flex justify-between items-start">
                  <span className="text-gray-600 font-medium">Order ID</span>
                  <span className="font-semibold text-gray-900 text-right text-xs">{payDetails.orderId}</span>
                </div>
              )}
              {payDetails?.razorpayPaymentId && (
                <div className="flex justify-between items-start">
                  <span className="text-gray-600 font-medium">Payment ID</span>
                  <span className="font-semibold text-gray-900 text-right text-xs">{payDetails.razorpayPaymentId}</span>
                </div>
              )}
              {payDetails?.billNo && (
                <div className="flex justify-between items-start">
                  <span className="text-gray-600 font-medium">Bill No</span>
                  <span className="font-semibold text-gray-900 text-right">{payDetails.billNo}</span>
                </div>
              )}
              {payDetails?.receiptNo && (
                <div className="flex justify-between items-start">
                  <span className="text-gray-600 font-medium">Receipt No</span>
                  <span className="font-semibold text-gray-900 text-right">{payDetails.receiptNo}</span>
                </div>
              )}
              <div className="flex justify-between items-start">
                <span className="text-gray-600 font-medium">Status</span>
                <span className={`font-semibold text-right ${payDetails?.paymentStatus === "Paid" ? "text-green-600" : "text-orange-500"}`}>{payDetails?.paymentStatus === "Paid" ? "Paid" : "Pending"}</span>
              </div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button size="lg" className="w-full sm:flex-1" onClick={() => { setShowThankYou(false); navigate("/packages") }}>Browse More Packages</Button>
            <Button size="lg" variant="outline" className="w-full sm:flex-1" onClick={() => navigate("/")}>Go to Home</Button>
          </div>
        </div>
      </div>
    )
  }

  const features = Array.isArray(pkg.services) ? pkg.services.map(s => s.servName) : []

  return (
    <>
      <Helmet>
        <title>{pkg.servName} | Pranaam Hospitals</title>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Breadcrumb */}
        <div className="bg-white border-b">
          <div className="container mx-auto px-4 py-4">
            <button onClick={() => navigate("/packages")} className="flex items-center gap-2 text-sm text-gray-600 hover:text-primary transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Back to Packages
            </button>
          </div>
        </div>

        {/* Hero */}
        <section className="bg-white pb-8">
          <div className="container mx-auto px-4 py-8 md:py-12">
            <div className="max-w-6xl mx-auto">
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 md:p-10">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xs font-medium text-primary bg-primary/10 rounded-full px-3 py-1">Health Package</span>
                      <span className="text-xs text-gray-500">{pkg.hospitalName}</span>
                    </div>
                    <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">{pkg.servName}</h1>
                    <p className="text-gray-500 text-sm mb-4">Comprehensive healthcare package designed for your well-being.</p>
                    <div className="flex items-center gap-3 text-sm text-gray-600">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      <span>{features.length} Tests & Consultations Included</span>
                    </div>
                  </div>
                  <div className="flex flex-col items-start md:items-end gap-4">
                    <div className="bg-gray-50 rounded-xl px-6 py-4">
                      <p className="text-xs text-gray-500 mb-1">Package Price</p>
                      <span className="text-3xl font-bold text-primary">₹{pkg.serviceCost}/-</span>
                    </div>
                    <button
                      onClick={handleBookNow}
                      className="inline-flex items-center justify-center gap-2 bg-primary text-white font-semibold py-3 px-8 rounded-xl hover:bg-primary/90 transition-colors text-sm w-full md:w-auto"
                    >
                      <Calendar className="w-4 h-4" />
                      Book Now
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features / Tests Included */}
        <section className="py-10 md:py-14">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6">Package Inclusions ({features.length})</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {features.map((feature, i) => (
                  <div key={i} className="flex items-start gap-3 bg-white rounded-xl p-4 border border-gray-100">
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">{feature.trim()}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose */}
        <section className="py-10 md:py-14 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6">Why Choose Pranaam Hospitals</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { icon: Shield, title: "NABH Accredited", desc: "Quality assured healthcare standards" },
                  { icon: Users, title: "Expert Doctors", desc: "Experienced medical professionals" },
                  { icon: Star, title: "Advanced Labs", desc: "State-of-the-art diagnostic facilities" },
                  { icon: Clock, title: "Quick Reports", desc: "Fast and accurate test results" },
                ].map((item, i) => (
                  <div key={i} className="text-center p-5 rounded-xl border border-gray-100 bg-gray-50">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                      <item.icon className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="font-semibold text-gray-900 text-sm mb-1">{item.title}</h3>
                    <p className="text-xs text-gray-500">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Phone Number Popup */}
      <Dialog open={showPhonePopup} onOpenChange={setShowPhonePopup}>
        <DialogContent className="sm:max-w-[550px] p-0 overflow-hidden [&>button]:hidden">
          <div className="relative">
            <button onClick={() => setShowPhonePopup(false)} className="absolute right-4 top-4 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors z-10">
              <X className="w-4 h-4" />
            </button>
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-3">Confirm your booking</h2>
              <div className="mb-2">
                <p className="text-gray-700">Package: <span className="text-primary font-semibold">{pkg?.servName}</span></p>
              </div>
              <div className="flex items-center justify-between mb-6">
                <p className="text-gray-700">Price: <span className="font-semibold">₹{pkg?.serviceCost}</span></p>
              </div>
              <p className="text-gray-600 text-sm mb-4">Please enter the registered mobile number for the specific patient.</p>
              <div className="flex flex-col sm:flex-row gap-3 items-stretch">
                <div className="flex items-center flex-1 border rounded-md overflow-hidden h-11">
                  <div className="flex items-center gap-2 px-3 bg-gray-50 h-full border-r">
                    <img src="https://flagcdn.com/w20/in.png" alt="India" width={20} height={15} className="rounded-sm" />
                    <span className="text-gray-600 text-sm font-medium">+91</span>
                  </div>
                  <Input type="tel" placeholder="Enter Mobile Number" value={mobileNumber}
                    onChange={(e) => { setMobileNumber(e.target.value.replace(/\D/g, '').slice(0, 10)); if (phoneError) setPhoneError("") }}
                    className="border-0 h-full focus-visible:ring-0 focus-visible:ring-offset-0" maxLength={10} />
                </div>
                {phoneError && <p className="text-red-600 text-xs">{phoneError}</p>}
                <Button onClick={handlePhoneConfirm} className="px-6 h-11 whitespace-nowrap w-full sm:w-auto">Confirm Booking</Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* OTP Popup */}
      <Dialog open={showOtpPopup} onOpenChange={setShowOtpPopup}>
        <DialogContent className="sm:max-w-[550px] p-0 overflow-hidden [&>button]:hidden">
          <div className="relative">
            <button onClick={() => setShowOtpPopup(false)} className="absolute right-4 top-4 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors z-10">
              <X className="w-4 h-4" />
            </button>
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-3">Confirm OTP</h2>
              <div className="mb-2">
                <p className="text-gray-700">Package: <span className="text-primary font-semibold">{pkg?.servName}</span></p>
              </div>
              <p className="text-gray-600 text-sm mb-4">We have sent you an OTP on {mobileNumber}.</p>
              {otpError && <p className="text-red-600 text-sm mb-2 font-medium">{otpError}</p>}
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-800 mb-2">Enter OTP<span className="text-red-500">*</span></label>
                <div className="flex gap-3">
                  <Input type="text" placeholder="Please Enter OTP" value={otpValue}
                    onChange={(e) => setOtpValue(e.target.value.replace(/\D/g, '').slice(0, 6))} className="flex-1" maxLength={6} />
                  <Button onClick={handleOtpConfirm} className="px-6">Confirm OTP</Button>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-600 text-sm">Still not received OTP?</span>
                {timer > 0 ? (
                  <span className="text-gray-500 text-sm">Resend in {timer}s</span>
                ) : (
                  <Button variant="link" size="sm" className="text-primary p-0 h-auto" onClick={handleResendOTP}>Resend OTP</Button>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Patient Select Popup */}
      <Dialog open={showPatientSelectPopup} onOpenChange={setShowPatientSelectPopup}>
        <DialogContent className="w-[95vw] sm:max-w-[700px] max-h-[85vh] p-0 [&>button]:hidden">
          <div className="relative overflow-y-auto max-h-[85vh]">
            <div className="sticky top-0 right-0 z-10 flex justify-end p-3">
              <button onClick={() => setShowPatientSelectPopup(false)} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-4 pb-4 sm:px-6 sm:pb-6 -mt-4">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">Confirm your booking</h2>
              <p className="text-sm text-gray-700 mb-1">Package: <span className="text-primary font-semibold">{pkg?.servName}</span></p>
              <p className="text-sm text-gray-700 mb-2">Price: <span className="font-semibold">₹{pkg?.serviceCost}</span></p>
              <p className="text-gray-600 text-xs mb-3">Please select appointment date and patient to proceed.</p>
              <div className="mb-3">
                <label className="block text-sm font-semibold text-gray-800 mb-1">Appointment Date<span className="text-red-500">*</span></label>
                <Input type="date" value={appointmentDate} onChange={(e) => setAppointmentDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]} className="w-full" required />
              </div>
              <div className="overflow-x-auto mb-3">
                <table className="w-full min-w-[420px] border border-gray-200 rounded-lg overflow-hidden">
                  <thead className="bg-primary text-white">
                    <tr>
                      <th className="px-2 sm:px-3 py-2 text-left text-xs font-medium w-10">Select</th>
                      <th className="px-2 sm:px-3 py-2 text-left text-xs font-medium">Patient Name</th>
                      <th className="px-2 sm:px-3 py-2 text-left text-xs font-medium">UID</th>
                      <th className="px-2 sm:px-3 py-2 text-left text-xs font-medium">Reg. Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {savedPatients.map((patient) => (
                      <tr key={patient.id} className={`border-t cursor-pointer hover:bg-gray-50 ${selectedPatient?.id === patient.id ? 'bg-primary/5' : ''}`}
                        onClick={() => setSelectedPatient(patient)}>
                        <td className="px-2 sm:px-3 py-2">
                          <input type="radio" name="patientSelect" checked={selectedPatient?.id === patient.id}
                            onChange={() => setSelectedPatient(patient)} className="w-4 h-4 text-primary accent-primary" />
                        </td>
                        <td className="px-2 sm:px-3 py-2 text-xs font-medium whitespace-nowrap">{patient.name}</td>
                        <td className="px-2 sm:px-3 py-2 text-xs text-gray-600 whitespace-nowrap">{patient.uid}</td>
                        <td className="px-2 sm:px-3 py-2 text-xs text-gray-600 whitespace-nowrap">{patient.regDate}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2">
                <Button onClick={() => setShowRegistrationPopup(true)} size="sm" className="text-xs sm:text-sm">Add a new patient</Button>
                {selectedPatient && appointmentDate && (
                  <Button onClick={handleConfirmBooking} size="sm" className="text-xs sm:text-sm">Confirm Booking</Button>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Registration Popup */}
      <Dialog open={showRegistrationPopup} onOpenChange={setShowRegistrationPopup}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto p-0 [&>button]:hidden">
          <div className="relative">
            <button onClick={() => setShowRegistrationPopup(false)} className="absolute right-4 top-4 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors z-10">
              <X className="w-4 h-4" />
            </button>
            <div className="p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Add New Patient</h2>
              <p className="text-gray-600 mb-6">Register a new patient to proceed with the booking.</p>
              <form onSubmit={handleRegistrationSubmit} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">First Name<span className="text-red-500">*</span></label>
                    <Input placeholder="First name" value={registrationData.firstName}
                      onChange={(e) => setRegistrationData(prev => ({ ...prev, firstName: e.target.value.replace(/[^a-zA-Z\s]/g, '') }))} required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Name<span className="text-red-500">*</span></label>
                    <Input placeholder="Last name" value={registrationData.lastName}
                      onChange={(e) => setRegistrationData(prev => ({ ...prev, lastName: e.target.value.replace(/[^a-zA-Z\s]/g, '') }))} required />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth<span className="text-red-500">*</span></label>
                  <Input type={registrationData.dob ? "date" : "text"}
                    onFocus={(e) => e.target.type = "date"} onBlur={(e) => { if (!e.target.value) e.target.type = "text" }}
                    placeholder="Date of Birth" value={registrationData.dob}
                    onChange={(e) => setRegistrationData(prev => ({ ...prev, dob: e.target.value }))} required max={new Date().toISOString().split('T')[0]} />
                </div>
                {!signupMode && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Relation<span className="text-red-500">*</span></label>
                    <select required value={registrationData.relation}
                      onChange={(e) => setRegistrationData(prev => ({ ...prev, relation: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary">
                      <option value="">Select Relation</option>
                      {relations.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">State<span className="text-red-500">*</span></label>
                  <select required value={registrationData.stateId}
                    onChange={(e) => setRegistrationData(prev => ({ ...prev, stateId: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary">
                    <option value="">Select State</option>
                    {states.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Gender<span className="text-red-500">*</span></label>
                  <Select value={registrationData.gender} onValueChange={(val) => setRegistrationData(prev => ({ ...prev, gender: val }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Male">Male</SelectItem>
                      <SelectItem value="Female">Female</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" className="w-full">Submit</Button>
              </form>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Not Found Popup */}
      {showNotFoundPopup && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center" style={{ pointerEvents: "auto" }}>
          <div className="fixed inset-0 bg-black/80" onClick={() => setShowNotFoundPopup(false)} />
          <div className="relative bg-white rounded-lg w-[90vw] max-w-[350px] p-6" style={{ zIndex: 10000, pointerEvents: "auto" }}>
            <div className="text-center space-y-4">
              <p className="text-sm font-medium text-gray-800">Mobile number not found! Do you want to register?</p>
              <div className="flex justify-center gap-3">
                <button type="button" className="px-6 py-2 border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-50"
                  onClick={() => setShowNotFoundPopup(false)}>No</button>
                <button type="button" className="px-6 py-2 bg-red-700 hover:bg-red-800 text-white rounded-md text-sm font-medium"
                  onClick={handleRegisterYes}>Yes</button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Payment Details Popup */}
      <Dialog open={showPaymentPopup} onOpenChange={setShowPaymentPopup}>
        <DialogContent className="sm:max-w-[450px] max-h-[90vh] overflow-y-auto p-0 [&>button]:hidden">
          <div className="relative">
            <button onClick={() => setShowPaymentPopup(false)}
              className="absolute right-2 top-2 w-4 h-4 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors z-10">
              <X className="w-4 h-4" />
            </button>
            <div className="p-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Payment Details</h2>
              <div className="space-y-3 pb-4 border-b">
                <div className="flex justify-between">
                  <span className="text-gray-600">Patient Name</span>
                  <span className="font-semibold text-gray-900">{bookingDetails?.patientName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">UID</span>
                  <span className="font-semibold text-gray-900">{bookingDetails?.patientId}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Package</span>
                  <span className="font-semibold text-gray-900">{bookingDetails?.packageName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Location</span>
                  <span className="font-semibold text-gray-900">{bookingDetails?.location}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Date</span>
                  <span className="font-semibold text-gray-900">{bookingDetails?.appointmentDate}</span>
                </div>
              </div>
              <div className="py-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Package Fee</span>
                  <span className="font-semibold text-gray-900">Rs. {bookingDetails?.packagePrice}/-</span>
                </div>
                <div className="flex justify-between pt-2 border-t">
                  <span className="font-semibold text-primary">Total Amount</span>
                  <span className="font-bold text-primary">Rs. {bookingDetails?.packagePrice}/-</span>
                </div>
              </div>
              <div className="flex gap-3">
                <Button onClick={handlePaymentComplete} className="w-full py-6 text-lg" disabled={payLoading}>
                  {payLoading && payMode === "online" ? "Processing..." : `Pay Rs. ${bookingDetails?.packagePrice}/-`}
                </Button>
                {/* <Button variant="outline" onClick={handlePayAtHospital}
                  className="flex-1 py-6 text-lg border-primary text-primary hover:bg-primary/5" disabled={payLoading}>
                  {payLoading && payMode === "hospital" ? "Processing..." : "Pay at Hospital"}
                </Button> */}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
